import * as flatbuffers from 'flatbuffers';
import { Repeater } from '@repeaterjs/repeater';
import { calcTreeSize, DEFAULT_NODE_SIZE, NODE_ITEM_LEN, streamSearch, } from './packedrtree.js';
import { magicbytes, SIZE_PREFIX_LEN } from './constants.js';
import Config from './Config.js';
import Logger from './Logger.js';
import HeaderMeta from './HeaderMeta.js';
import { Feature } from './flat-geobuf/feature.js';
export class HttpReader {
    constructor(headerClient, header, headerLength, indexLength) {
        this.headerClient = headerClient;
        this.header = header;
        this.headerLength = headerLength;
        this.indexLength = indexLength;
    }
    static async open(url) {
        const assumedHeaderLength = 2024;
        const headerClient = new BufferedHttpRangeClient(url);
        const assumedIndexLength = (() => {
            const assumedBranchingFactor = DEFAULT_NODE_SIZE;
            const prefetchedLayers = 3;
            let result = 0;
            let i;
            for (i = 0; i < prefetchedLayers; i++) {
                const layer_width = assumedBranchingFactor ** i * NODE_ITEM_LEN;
                result += layer_width;
            }
            return result;
        })();
        const minReqLength = assumedHeaderLength + assumedIndexLength;
        Logger.debug(`fetching header. minReqLength: ${minReqLength} (assumedHeaderLength: ${assumedHeaderLength}, assumedIndexLength: ${assumedIndexLength})`);
        {
            const bytes = new Uint8Array(await headerClient.getRange(0, 8, minReqLength, 'header'));
            if (!bytes.subarray(0, 3).every((v, i) => magicbytes[i] === v)) {
                Logger.error(`bytes: ${bytes} != ${magicbytes}`);
                throw new Error('Not a FlatGeobuf file');
            }
            Logger.debug('magic bytes look good');
        }
        let headerLength;
        {
            const bytes = await headerClient.getRange(8, 4, minReqLength, 'header');
            headerLength = new DataView(bytes).getUint32(0, true);
            const HEADER_MAX_BUFFER_SIZE = 1048576 * 10;
            if (headerLength > HEADER_MAX_BUFFER_SIZE || headerLength < 8) {
                throw new Error('Invalid header size');
            }
            Logger.debug(`headerLength: ${headerLength}`);
        }
        const bytes = await headerClient.getRange(12, headerLength, minReqLength, 'header');
        const bb = new flatbuffers.ByteBuffer(new Uint8Array(bytes));
        const header = HeaderMeta.fromByteBuffer(bb);
        const indexLength = calcTreeSize(header.featuresCount, header.indexNodeSize);
        Logger.debug('completed: opening http reader');
        return new HttpReader(headerClient, header, headerLength, indexLength);
    }
    async *selectBbox(rect) {
        const lengthBeforeTree = this.lengthBeforeTree();
        const bufferedClient = this.headerClient;
        const readNode = async function (offsetIntoTree, size) {
            const minReqLength = 0;
            return bufferedClient.getRange(lengthBeforeTree + offsetIntoTree, size, minReqLength, 'index');
        };
        const batches = [];
        let currentBatch = [];
        for await (const searchResult of streamSearch(this.header.featuresCount, this.header.indexNodeSize, rect, readNode)) {
            const [featureOffset, ,] = searchResult;
            let [, , featureLength] = searchResult;
            if (!featureLength) {
                Logger.info('final feature');
                const guessLength = Config.global.extraRequestThreshold();
                featureLength = guessLength;
            }
            if (currentBatch.length == 0) {
                currentBatch.push([featureOffset, featureLength]);
                continue;
            }
            const prevFeature = currentBatch[currentBatch.length - 1];
            const gap = featureOffset - (prevFeature[0] + prevFeature[1]);
            if (gap > Config.global.extraRequestThreshold()) {
                Logger.info(`Pushing new feature batch, since gap ${gap} was too large`);
                batches.push(currentBatch);
                currentBatch = [];
            }
            currentBatch.push([featureOffset, featureLength]);
        }
        this.headerClient.logUsage('header+index');
        if (currentBatch.length > 0) {
            batches.push(currentBatch);
        }
        const promises = batches.flatMap((batch) => this.readFeatureBatch(batch));
        yield* Repeater.merge(promises);
    }
    lengthBeforeTree() {
        return magicbytes.length + SIZE_PREFIX_LEN + this.headerLength;
    }
    lengthBeforeFeatures() {
        return this.lengthBeforeTree() + this.indexLength;
    }
    buildFeatureClient() {
        return new BufferedHttpRangeClient(this.headerClient.httpClient);
    }
    async *readFeatureBatch(batch) {
        const [firstFeatureOffset] = batch[0];
        const [lastFeatureOffset, lastFeatureLength] = batch[batch.length - 1];
        const batchStart = firstFeatureOffset;
        const batchEnd = lastFeatureOffset + lastFeatureLength;
        const batchSize = batchEnd - batchStart;
        const featureClient = this.buildFeatureClient();
        for (const [featureOffset] of batch) {
            yield await this.readFeature(featureClient, featureOffset, batchSize);
        }
        featureClient.logUsage('feature');
    }
    async readFeature(featureClient, featureOffset, minFeatureReqLength) {
        const offset = featureOffset + this.lengthBeforeFeatures();
        let featureLength;
        {
            const bytes = await featureClient.getRange(offset, 4, minFeatureReqLength, 'feature length');
            featureLength = new DataView(bytes).getUint32(0, true);
        }
        const byteBuffer = await featureClient.getRange(offset + 4, featureLength, minFeatureReqLength, 'feature data');
        const bytes = new Uint8Array(byteBuffer);
        const bytesAligned = new Uint8Array(featureLength + SIZE_PREFIX_LEN);
        bytesAligned.set(bytes, SIZE_PREFIX_LEN);
        const bb = new flatbuffers.ByteBuffer(bytesAligned);
        bb.setPosition(SIZE_PREFIX_LEN);
        return Feature.getRootAsFeature(bb);
    }
}
class BufferedHttpRangeClient {
    constructor(source) {
        this.bytesEverUsed = 0;
        this.bytesEverFetched = 0;
        this.buffer = new ArrayBuffer(0);
        this.head = 0;
        if (typeof source === 'string') {
            this.httpClient = new HttpRangeClient(source);
        }
        else {
            this.httpClient = source;
        }
    }
    async getRange(start, length, minReqLength, purpose) {
        this.bytesEverUsed += length;
        const start_i = start - this.head;
        const end_i = start_i + length;
        if (start_i >= 0 && end_i <= this.buffer.byteLength) {
            return this.buffer.slice(start_i, end_i);
        }
        const lengthToFetch = Math.max(length, minReqLength);
        this.bytesEverFetched += lengthToFetch;
        Logger.debug(`requesting for new Range: ${start}-${start + length - 1}`);
        this.buffer = await this.httpClient.getRange(start, lengthToFetch, purpose);
        this.head = start;
        return this.buffer.slice(0, length);
    }
    logUsage(purpose) {
        const category = purpose.split(' ')[0];
        const used = this.bytesEverUsed;
        const requested = this.bytesEverFetched;
        const efficiency = ((100.0 * used) / requested).toFixed(2);
        Logger.info(`${category} bytes used/requested: ${used} / ${requested} = ${efficiency}%`);
    }
}
class HttpRangeClient {
    constructor(url) {
        this.requestsEverMade = 0;
        this.bytesEverRequested = 0;
        this.url = url;
    }
    async getRange(begin, length, purpose) {
        this.requestsEverMade += 1;
        this.bytesEverRequested += length;
        const range = `bytes=${begin}-${begin + length - 1}`;
        Logger.info(`request: #${this.requestsEverMade}, purpose: ${purpose}), bytes: (this_request: ${length}, ever: ${this.bytesEverRequested}), Range: ${range}`);
        const response = await fetch(this.url, {
            headers: {
                Range: range,
            },
        });
        return response.arrayBuffer();
    }
}
//# sourceMappingURL=HttpReader.js.map